import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPost, performBlogAction } from '../../services/blogService.ts';
import type { BlogPost } from '../../types/blog.ts';

export default function BlogPostAdminScreen() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    console.log('[BlogPostAdminScreen] Component mounted, slug:', slug);
    
    if (slug) {
      loadPost();
    }
  }, [slug]);

  const loadPost = async () => {
    if (!slug) return;
    
    try {
      console.log('[BlogPostAdminScreen] Loading post:', slug);
      setLoading(true);
      setError(null);
      const data = await getPost(slug);
      if (!data) {
        setError('Post not found');
        return;
      }
      setPost(data);
      console.log('[BlogPostAdminScreen] Loaded post:', data.title);
    } catch (err) {
      console.error('[BlogPostAdminScreen] Error loading post:', err);
      setError(err instanceof Error ? err.message : 'Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: 'publish' | 'archive' | 'delete') => {
    if (!slug) return;
    
    if (action === 'delete') {
      const confirmed = confirm('Are you sure you want to delete this post? This action cannot be undone.');
      if (!confirmed) return;
    }

    try {
      console.log('[BlogPostAdminScreen] Performing action:', action, 'on post:', slug);
      setActionLoading(action);
      await performBlogAction({ type: action, slug });
      console.log('[BlogPostAdminScreen] Action completed');
      
      if (action === 'delete') {
        window.location.href = '/admin/blog';
      } else {
        await loadPost(); // Reload to show updated status
      }
    } catch (err) {
      console.error('[BlogPostAdminScreen] Action failed:', err);
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = 'inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium';
    switch (status) {
      case 'published':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'draft':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'archived':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const renderMarkdown = (markdown: string) => {
    // Simple markdown rendering - in production, use a proper markdown library
    return markdown
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('# ')) {
          return <h1 key={index} className="text-3xl font-bold mb-4">{line.slice(2)}</h1>;
        }
        if (line.startsWith('## ')) {
          return <h2 key={index} className="text-2xl font-semibold mb-3">{line.slice(3)}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={index} className="text-xl font-medium mb-2">{line.slice(4)}</h3>;
        }
        if (line.trim() === '') {
          return <br key={index} />;
        }
        return <p key={index} className="mb-2">{line}</p>;
      });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error && !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/admin/blog"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Back to Blog Admin
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white shadow sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-2xl font-bold text-gray-900">{post?.title}</h1>
                  <span className={getStatusBadge(post?.status || 'draft')}>
                    {post?.status}
                  </span>
                </div>
                <p className="text-gray-600 mb-3">{post?.excerpt}</p>
                <div className="text-sm text-gray-500 space-y-1">
                  <p><strong>Author:</strong> {post?.author}</p>
                  <p><strong>Slug:</strong> /{post?.slug}</p>
                  <p><strong>Created:</strong> {formatDate(post?.created_at || null)}</p>
                  <p><strong>Updated:</strong> {formatDate(post?.updated_at || null)}</p>
                  <p><strong>Published:</strong> {formatDate(post?.published_at || null)}</p>
                  {post?.tags && post.tags.length > 0 && (
                    <p><strong>Tags:</strong> {post.tags.join(', ')}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-col space-y-2 ml-6">
                <Link
                  to="/admin/blog"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  ← Back to List
                </Link>
                {post?.status === 'published' && (
                  <Link
                    to={`/blog/${post.slug}`}
                    target="_blank"
                    className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
                  >
                    View Public Post
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="bg-white shadow sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
            <div className="flex space-x-3">
              {post?.status === 'draft' && (
                <button
                  onClick={() => handleAction('publish')}
                  disabled={actionLoading === 'publish'}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  {actionLoading === 'publish' ? 'Publishing...' : 'Publish Post'}
                </button>
              )}
              {post?.status === 'published' && (
                <button
                  onClick={() => handleAction('archive')}
                  disabled={actionLoading === 'archive'}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50"
                >
                  {actionLoading === 'archive' ? 'Archiving...' : 'Archive Post'}
                </button>
              )}
              <button
                onClick={() => handleAction('delete')}
                disabled={actionLoading === 'delete'}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading === 'delete' ? 'Deleting...' : 'Delete Post'}
              </button>
            </div>
          </div>
        </div>

        {/* Content Preview */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Content Preview</h3>
            {post?.cover_url && (
              <div className="mb-6">
                <img 
                  src={post.cover_url} 
                  alt="Cover" 
                  className="w-full h-64 object-cover rounded-lg"
                />
              </div>
            )}
            <div className="prose max-w-none">
              <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {post?.content_md}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 